import { useForm, useFieldArray } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation, useQueryClient, useQuery } from "@tanstack/react-query";
import { createAccessRule, getDbtModelsFilters } from "@/helpers/apis";
import {
  Button,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  Form,
  TooltipContent,
  TooltipCore,
  TooltipTrigger,
} from "@altimateai/lego";
import { useState, useMemo } from "react";
import { LoadingButton, Stack } from "@altimateai/lego";
import {
  AccessRulesFormSchema,
  AccessRulesFormValues,
  UserGroupEntities,
  UserGroupRuleTypes,
} from "./types";
import { toast } from "@altimateai/lego";
import { AddIcon } from "@/assets/icons";
import { IconTrash } from "@tabler/icons-react";
import { useAppState } from "@/modules/app/useAppContext";
import AccessRuleValueField from "./AccessRuleValueField";
import AccessRuleFormFields from "./AccessRuleFormFields";
import { formatAccessRulePayload } from "./accessRuleHelpers";
import { useAccessRuleCoverage } from "./useAccessRuleCoverage";

interface Props {
  groupId: string;
}

const AddNewAccessRule = ({ groupId }: Props) => {
  const { datastores, enableCostAttributionInTeams, isDatabricksTenant } = useAppState();
  const [open, setOpen] = useState(false);
  const queryClient = useQueryClient();

  // Databricks tenants default new rules to workspace scoping; Snowflake to user.
  const defaultEntityType = isDatabricksTenant
    ? UserGroupEntities.WORKSPACE
    : UserGroupEntities.USER;
  const makeDefaultRule = () => ({
    entity_type: defaultEntityType,
    match_type: UserGroupRuleTypes.EXACT,
    entity: "",
    key: "",
  });

  const form = useForm<AccessRulesFormValues>({
    resolver: zodResolver(AccessRulesFormSchema),
    defaultValues: {
      rules: [makeDefaultRule()],
    },
  });

  const watchedRules = form.watch("rules");
  useAccessRuleCoverage(enableCostAttributionInTeams ? watchedRules : []);

  // Check if any rule has DBT_INTEGRATION type
  const hasDbtIntegrationRule = form
    .watch("rules")
    .some(rule => rule.entity_type === UserGroupEntities.DBT_INTEGRATION);

  // Fetch DBT integrations when needed
  const { data: dbtIntegrationsResponse, isPending: isDbtIntegrationsLoading } = useQuery({
    queryKey: ["dbtModelsFiltersResponse"],
    queryFn: getDbtModelsFilters,
    enabled: hasDbtIntegrationRule, // Enable when we have a DBT_INTEGRATION rule
  });

  // Prepare datastore options
  const datastoreOptions = useMemo(() => {
    if (!datastores) return [];
    return datastores.map(datastore => ({
      label: datastore.name,
      value: (datastore.instance_id ?? "").toString(),
    }));
  }, [datastores]);

  // Prepare dbt integration options
  const dbtIntegrationOptions = useMemo(() => {
    if (!dbtIntegrationsResponse?.dbt_integrations) return [];
    return dbtIntegrationsResponse.dbt_integrations.map(integration => ({
      label: integration.label,
      value: integration.value,
    }));
  }, [dbtIntegrationsResponse]);

  const { fields, append, remove } = useFieldArray({
    name: "rules",
    control: form.control,
  });

  const { mutate: createRules, isPending: isLoading } = useMutation({
    mutationFn: async (data: AccessRulesFormValues) => {
      // Format all rules for the API call
      // Send a single API call with all rules
      await createAccessRule(groupId, {
        rules: formatAccessRulePayload(data.rules),
      });
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["UserGroupDetails", groupId] });
      toast({ description: "Access rules created successfully" });
      setOpen(false);
      form.reset({
        rules: [makeDefaultRule()],
      });
    },
    onError: (error: Error) => {
      toast({
        description: `Failed to create access rules: ${error.message || error}`,
        variant: "destructive",
      });
    },
  });

  const validateForm = async (): Promise<boolean> => {
    const isValid = await form.trigger();
    return isValid;
  };

  const onSubmit = async (data: AccessRulesFormValues) => {
    const isValid = await validateForm();
    if (!isValid) {
      toast({
        description: "Please fill in all required fields before creating rules",
        variant: "destructive",
      });
      return;
    }
    createRules(data);
  };

  const addRule = () => {
    append(makeDefaultRule());
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <TooltipCore>
        <DialogTrigger asChild>
          <TooltipTrigger asChild>
            <Button variant="outline" size="sm" aria-label="Add new access rule">
              <AddIcon className="al-h-4 al-w-4" />
            </Button>
          </TooltipTrigger>
        </DialogTrigger>
        <TooltipContent>Add New Access Rule</TooltipContent>
      </TooltipCore>
      <DialogContent className="al-max-w-[560px]">
        <DialogHeader>
          <DialogTitle>Create New Access Rule</DialogTitle>
        </DialogHeader>
        {/* {enableCostAttributionInTeams && (
          <AccessRuleCoverageBanner
            coverageSummary={coverageSummary}
            isCoverageLoading={isCoverageLoading}
            shouldPreviewCoverage={shouldPreviewCoverage}
            coverageError={coverageError}
            refreshCoverage={handleRefreshCoverage}
          />
        )} */}
        <Form {...form}>
          {/* eslint-disable-next-line jsx-a11y/no-redundant-roles */}
          <form onSubmit={form.handleSubmit(onSubmit)} className="al-space-y-4" role="form">
            <div className="al-max-h-[60vh] al-overflow-y-auto al-px-2">
              <Stack direction="column" gap="xl">
                {fields.map((field, index) => (
                  <div key={field.id}>
                    <Stack direction="row" align="center" justify="between">
                      <p className="al-text-sm al-font-medium">Rule {index + 1}</p>
                      {fields.length > 1 && (
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => remove(index)}
                          className="al-p-1 al-h-8 al-w-8"
                        >
                          <IconTrash size={16} className="al-text-destructive" />
                        </Button>
                      )}
                    </Stack>
                    <AccessRuleFormFields form={form} index={index} />
                    <div className="al-mt-2">
                      <AccessRuleValueField
                        form={form}
                        index={index}
                        entityType={form.watch(`rules.${index}.entity_type`)}
                        matchType={form.watch(`rules.${index}.match_type`)}
                        datastoreOptions={datastoreOptions}
                        dbtIntegrationOptions={dbtIntegrationOptions}
                        isDbtIntegrationsLoading={isDbtIntegrationsLoading}
                      />
                    </div>

                    <div className="al-my-4">
                      <div className="al-border-t" />
                      <p className="al-text-xs al-text-muted-foreground al-uppercase al-mt-2">
                        AND
                      </p>
                    </div>
                  </div>
                ))}

                <Button type="button" variant="outline" onClick={addRule} className="al-w-full">
                  <AddIcon className="al-h-4 al-w-4 al-mr-2" />
                  Add Another Rule
                </Button>
              </Stack>
            </div>
            <Stack justify="end" className="al-mt-4">
              <Button type="button" variant="outline" onClick={() => setOpen(false)}>
                Cancel
              </Button>
              <LoadingButton type="submit" loading={isLoading}>
                {isLoading ? "Creating..." : "Create Rules"}
              </LoadingButton>
            </Stack>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
};

export default AddNewAccessRule;
